





















































import {
    isMobileLayout,
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    MaintenanceChecker,
    SdsSectionTitle,
    SdsIllustration,
    SpinnerContainer,
} from '@superbet-group/web.lib.ui-components';
import {
    computed,
    defineComponent,
    PropType,
    ref,
    watch,
} from '@vue/composition-api';

import { CategoryWithCompetitions } from '@core/models/struct/CategoryWithCompetitions';
import SportCompetitionCard from './competitions/SportCompetitionCard.vue';
import CompetitionEmptyState from './placeholders/CompetitionEmptyState.vue';
import { MaintenanceType } from '@superbet-group/web.types';
import { MaintenanceStore } from '@store/modules/data/maintenance/maintenanceIndex';
import { amIPrerender } from '@src/app/utils/botUtils';
import { useSportPageTitle } from '@shared/composables/useSportPageTitle';

export default defineComponent({
    name: 'SportCompetitions',
    props: {
        sportId: {
            type: Number,
            required: true,
        },
        competitions: {
            type: Array as PropType<CategoryWithCompetitions[]>,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },
    setup(props) {
        const applicableMaintenanceTypes = [MaintenanceType.Prematch];
        const competitionListRef = ref<HTMLElement>();
        const expandedCategoryIds = ref<Set<number>>(new Set());

        const maintenanceStore = useVuexStore<MaintenanceStore>('data/maintenance');
        const { route } = useVueRouter();

        function toggleExpandedCategory(categoryId: number) {
            if (!expandedCategoryIds.value) {
                return;
            }
            if (expandedCategoryIds.value.has(categoryId)) {
                expandedCategoryIds.value.delete(categoryId);
            } else {
                expandedCategoryIds.value.add(categoryId);
            }
            expandedCategoryIds.value = new Set(expandedCategoryIds.value);
        }

        function getNavOffset(sourceEl: HTMLElement) {
            const headerTopString = getComputedStyle(sourceEl).getPropertyValue('--header-offset-top');

            if (headerTopString == null) {
                return 0;
            }

            const parsedOffset = parseInt(headerTopString, 10);

            return isNaN(parsedOffset) ? 0 : parsedOffset;
        }

        function isCategoryExpanded(categoryId: number) {
            if (amIPrerender()) {
                return true;
            }

            return expandedCategoryIds.value?.has(categoryId) || false;
        }

        function scrollToVisibleElement() {
            const visibleElementIndex = props.competitions.findIndex(
                (categoryWithCompetitions: CategoryWithCompetitions) => expandedCategoryIds.value?.has(categoryWithCompetitions.categoryId),
            );

            if (!visibleElementIndex || !competitionListRef.value) {
                return;
            }

            const visibleElement = competitionListRef.value.children[visibleElementIndex] as HTMLElement;

            if (!visibleElement) {
                return;
            }

            // We need the element position, offset on the app layer (propagated via CSS var) and just
            // some safety spacing to prettily scroll to the competition, so let's crunch!
            const elementTop = visibleElement.getBoundingClientRect().top;
            const offsetTop = getNavOffset(visibleElement);
            const readabilitySpacingPx = 16;

            window.scrollTo({
                top: Math.max(0, elementTop - offsetTop - readabilitySpacingPx),
            });
        }

        const { title } = useSportPageTitle();

        watch(
            () => [route.value?.query?.categoryId, props.competitions],
            ([categoryId, currentCompetitions], oldParams) => {
                if (!categoryId || currentCompetitions.length === 0) {
                    return;
                }

                const oldCategoryId = oldParams?.[0] ?? null;

                if (oldCategoryId && expandedCategoryIds.value?.has(parseInt(oldCategoryId as string, 10))) {
                    return;
                }

                expandedCategoryIds.value.add(parseInt(categoryId as string, 10));

                // This needs to be forced on the async queue to allow for rendering updates
                // and for the ref to be set
                setTimeout(() => scrollToVisibleElement(), 0);
            },
            {
                immediate: true,
                flush: 'post',
            },
        );

        return {
            title,
            isMobile: computed(() => isMobileLayout()),
            applicableMaintenanceTypes,
            isInMaintenance: maintenanceStore.getter('isInMaintenance'),
            competitionListRef,
            expandedCategoryIds,
            toggleExpandedCategory,
            isCategoryExpanded,
        };
    },
    components: {
        SdsSectionTitle,
        SdsIllustration,
        SpinnerContainer,
        MaintenanceChecker,
        SportCompetitionCard,
        CompetitionEmptyState,
    },
});
